import Page from "../Components/Page";
import ButtonIcon from "../Components/Buttons/ButtonIcon";
import EditFarmMenuButton from "../Components/EditFarmMenuButton";
import ManagedAcresTable from "../Components/Tables/ManagedAcresTable";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import NewMachineModal from "../Components/CropYearForms/FieldOperationsSubForms/NewMachineModal";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../Components/Auth/useAuth";
import { useEffect, useState } from "react";

import {
  axiosInstance,
  buildLegacyHeaders,
  fetchFarm,
} from "../Components/axiosFetchers";
import MachinesTable from "../Components/Tables/MachinesTable";
import {
  ResourceBreadcrumb,
  buildFarmBreadcrumb,
} from "../Components/ResourceBreadcrumb.jsx";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  DATA_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";

export default function FarmPage({ requireSelectedGrower = false }) {
  const { user } = useAuth();
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  let { farmId } = useParams();
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [farmFields, setFarmFields] = useState([]);
  const [farmMachines, setFarmMachines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canWriteSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_WRITE_SCOPE);
  const canUseSelectedGrowerWorkflow =
    !requireSelectedGrower ||
    (Boolean(selectedAggregatorGrowerPublicId) && canReadSelectedGrower);

  document.title = "Farm Page - Field To Market Canada";

  useEffect(() => {
    if (requireSelectedGrower && !canUseSelectedGrowerWorkflow) {
      setFarm(null);
      setFarmFields([]);
      setFarmMachines([]);
      setIsLoading(false);
      return;
    }

    const startupFarmPage = async () => {
      const nextFarm = await fetchFarm(farmId, user);

      const fieldsResponse = await axiosInstance.get(
        process.env.REACT_APP_API_URL + "/farms/" + farmId + "/fields",
        { headers: buildLegacyHeaders(user) }
      );
      setFarm(nextFarm);
      setFarmFields(fieldsResponse.data);
      setFarmMachines(nextFarm.machines || []);
      setIsLoading(false);
    };
    startupFarmPage();
  }, [
    canUseSelectedGrowerWorkflow,
    farmId,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
    user,
  ]);

  return (
    <Page
      title={farm?.name || "Farm"}
      showQuickFacts={false}
      headerBorderColor="border-[#34a853]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="px-4 pb-6 pt-5 md:px-6 md:pb-8"
      introContent={
        <p>{farmFields.length} fields / {farmMachines.length} equipment records</p>
      }
      headerActions={
        canUseSelectedGrowerWorkflow && canWriteSelectedGrower ? (
        <EditFarmMenuButton
          text={"Edit Farm"}
          farmName={farm?.name}
          onClick={() => navigate(`/farm/${farmId}/form`)}
        />
        ) : null
      }
    >
      <div className="flex h-full w-full flex-col gap-6 text-[#666666]">
        {requireSelectedGrower ? (
          <AggregatorSelectedGrowerPanel
            selectedGrower={selectedAggregatorGrower}
          />
        ) : null}
        {requireSelectedGrower &&
        selectedAggregatorGrowerPublicId &&
        !canReadSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link cannot view farm records."
            requiredScope={DATA_READ_SCOPE}
          />
        ) : null}
        {!canUseSelectedGrowerWorkflow ? null : (
          <>
            <ResourceBreadcrumb
              ariaLabel="Farm location"
              items={buildFarmBreadcrumb({
                farm,
                farmId,
                includeDashboard: true,
              })}
            />
            <ManagedAcresSection
              canWrite={canWriteSelectedGrower}
              fields={farmFields}
              isLoading={isLoading}
            />
            <EquipmentSection
              canWrite={canWriteSelectedGrower}
              machines={farmMachines}
              setFarmMachines={setFarmMachines}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </Page>
  );
}

function ManagedAcresSection({ canWrite = true, fields, isLoading }) {
  const [managedAcresCollapsed, setManagedAcresCollapsed] = useState(false);
  const navigate = useNavigate();
  let { farmId } = useParams();

  return (
    <div className="w-full overflow-hidden border-y border-slate-200 bg-white text-lg">
      <div
        className="flex w-full cursor-pointer items-center justify-between border-b border-slate-200 bg-[#fcfcfa] px-4 py-3"
        onClick={() => setManagedAcresCollapsed(!managedAcresCollapsed)}
      >
        <div className="flex items-center gap-4">
          <div className="flex h-9 w-9 items-center justify-center bg-[#fff1ea] text-[#f15d22]">
            <ViewQuiltIcon sx={{ fontSize: 23 }} />
          </div>
          <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Acreage
            </p>
            <p className="text-[20px] font-black text-slate-900">
              Managed Fields
            </p>
          </div>
        </div>
        <div
          className={`${
            !managedAcresCollapsed ? "rotate-180" : "rotate-0"
          } text-slate-500 transition-all duration-300`}
        >
          <KeyboardArrowDownIcon fontSize="large" />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-500 ${
          managedAcresCollapsed ? "max-h-0 opacity-0" : "max-h-[3000px] opacity-100"
        }`}
      >
        <div className="px-4 py-4">
          {canWrite ? (
          <div className="mb-4 flex w-full">
            <ButtonIcon
              text={"Add New Field"}
              onClick={() => navigate(`/farm/${farmId}/field/form`)}
              grow={false}
              Icon={AddCircleOutlineIcon}
            />
          </div>
          ) : null}
          <div className="w-full overflow-x-auto border border-slate-200 bg-white p-2">
            <ManagedAcresTable fields={fields} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}

function EquipmentSection({ canWrite = true, machines, isLoading, setFarmMachines }) {
  const { user } = useAuth();
  const [isNewMachineModalOpen, setIsNewMachineModalOpen] = useState(false);

  async function handleAddNewMachine(newMachine) {
    const response = await axiosInstance.post(
      process.env.REACT_APP_API_URL + `/machines`,
      {
        ...newMachine,
      },
      {
        headers: {
          token: "Bearer " + user.token,
          provider: user.provider,
          "Content-Type": "application/json",
        },
      }
    );

    setFarmMachines([...machines, response.data]);
  }

  const [equipmentSectionCollapsed, setEquipmentSectionCollapsed] =
    useState(false);

  return (
    <div className="w-full overflow-hidden border-y border-slate-200 bg-white text-lg">
      <div
        className="flex w-full cursor-pointer items-center justify-between border-b border-slate-200 bg-[#fcfcfa] px-4 py-3"
        onClick={() => setEquipmentSectionCollapsed(!equipmentSectionCollapsed)}
      >
        <div className="flex items-center gap-4">
          <div className="flex h-9 w-9 items-center justify-center bg-[#eef7fb] text-[#243245]">
            <AgricultureIcon sx={{ fontSize: 23 }} />
          </div>
          <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Machine Library
            </p>
            <p className="text-[20px] font-black text-slate-900">
              Farm Equipment
            </p>
          </div>
        </div>
        <div
          className={`${
            !equipmentSectionCollapsed ? "rotate-180" : "rotate-0"
          } text-slate-500 transition-all duration-300`}
        >
          <KeyboardArrowDownIcon fontSize="large" />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-500 ${
          equipmentSectionCollapsed ? "max-h-0 opacity-0" : "max-h-[3000px] opacity-100"
        }`}
      >
        <div className="px-4 py-4">
          {canWrite ? (
          <div className="mb-4 flex w-full">
            <ButtonIcon
              text={"Add New Machine"}
              onClick={() => setIsNewMachineModalOpen(true)}
              grow={false}
              Icon={AddCircleOutlineIcon}
            />
            <NewMachineModal
              open={isNewMachineModalOpen}
              handleClose={setIsNewMachineModalOpen}
              handleAddNewMachine={handleAddNewMachine}
              machineLabel={"Add a new machine"}
              isGeneric={true}
            />
          </div>
          ) : null}
          <div className="w-full overflow-x-auto border border-slate-200 bg-white p-2">
            <MachinesTable
              machines={machines}
              setMachines={setFarmMachines}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
